(function () {
  'use strict';

  var DATA = null;
  var UNIT_KEY = 'pedal:sizeUnit';
  var MEASURE_KEY = 'pedal:measurements';
  var SHOW_BRANDS = false;

  var BODY_LABEL = {
    chest: 'Chest',
    waist: 'Waist',
    hips: 'Hips',
    torso: 'Torso',
    palm: 'Hand circumference',
    finger: 'Hand length',
    arm: 'Upper arm',
    shoeMen: "US shoe size (men's)",
    shoeWomen: "US shoe size (women's)"
  };

  var BODY_HINT = {
    chest: 'Around the fullest part of your chest, at the armpit, arms lowered.',
    waist: 'Around your natural waist, at the narrowest point.',
    hips: 'Around the widest part of your hips and seat.',
    torso: 'From under the crotch, up your body, to the base of your neck.',
    palm: 'Around the widest point of your lightly closed hand.',
    finger: 'From the tip of your longest finger to the base of your palm.',
    arm: 'Around the widest part of your upper arm.',
    shoeMen: 'The US shoe size you normally wear.',
    shoeWomen: 'The US shoe size you normally wear.'
  };

  // ---------- units ----------

  function toCm(inches) { return inches * 2.54; }
  function toIn(cm) { return cm / 2.54; }

  function round(n, dp) {
    var f = Math.pow(10, dp == null ? 1 : dp);
    return Math.round(n * f) / f;
  }

  // Inches is the default on every visit. Pedal is a US store, so inches is what
  // most shoppers want to land on. The choice is held in sessionStorage rather
  // than localStorage, so switching to cm sticks while a shopper browses but
  // never becomes a permanent setting they cannot get back out of.
  function getUnit() {
    try {
      localStorage.removeItem(UNIT_KEY);
      return sessionStorage.getItem(UNIT_KEY) || 'in';
    } catch (e) {
      return 'in';
    }
  }

  function setUnit(u) {
    try { sessionStorage.setItem(UNIT_KEY, u); } catch (e) {}
  }

  // Format a stored inches value/range into the display unit.
  function fmt(val, unit, unitless) {
    if (val == null) return '—';
    if (typeof val === 'string') return val;
    if (!Array.isArray(val)) return String(val);

    if (unitless) {
      if (val.length === 1) return String(val[0]);
      if (val[0] == null) return 'Up to ' + val[1];
      if (val[1] == null) return val[0] + '+';
      return val[0] + ' – ' + val[1];
    }

    var conv = function (n) { return unit === 'cm' ? round(toCm(n), 1) : round(n, 2); };
    var suffix = unit === 'cm' ? ' cm' : '"';

    if (val.length === 1 || val[1] === undefined) return conv(val[0]) + suffix;
    if (val[0] == null) return 'Under ' + conv(val[1]) + suffix;
    if (val[1] == null) return 'Over ' + conv(val[0]) + suffix;
    if (val[0] === val[1]) return conv(val[0]) + suffix;
    return conv(val[0]) + ' – ' + conv(val[1]) + suffix;
  }

  // ---------- size name normalisation ----------
  // Pedal's catalogue spells the same size many ways: SMALL / Small / SML / S,
  // X-Large / X LARGE / XLG / XL, XXXX-Large / 4XL. Everything collapses to a rank
  // so a recommendation can be matched back to whatever the product calls it.
  function sizeRank(raw) {
    if (raw == null) return null;
    var s = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!s) return null;

    // Sock sizes such as "LXLSOCK1013" or "SMSOCK69"
    if (s.indexOf('SOCK') !== -1) {
      if (/^S(M|MALL)?/.test(s)) return 2;
      if (/^M(ED|EDIUM)?/.test(s)) return 3;
      if (/^L/.test(s)) return 4;
    }

    // Numeric forms: 2XL, 3XL, 4XL ...
    var m = s.match(/^(\d)X+L/);
    if (m) return 4 + parseInt(m[1], 10);

    // Explicit three letter codes used in the charts
    if (s === 'XXS') return 0;
    if (s === 'XSM' || s === 'XS') return 1;
    if (s === 'SML' || s === 'S') return 2;
    if (s === 'MED' || s === 'M') return 3;
    if (s === 'LRG' || s === 'L') return 4;
    if (s === 'XLG') return 5;

    // Counted X forms: XXSMALL, XSMALL, SMALL / XXLARGE, XLARGE, LARGE
    var xs = s.match(/^(X*)SMALL$/);
    if (xs) return 2 - xs[1].length;
    var xl = s.match(/^(X*)LARGE$/);
    if (xl) return 4 + xl[1].length;
    var xsAbbr = s.match(/^(X+)S$/);
    if (xsAbbr) return 2 - xsAbbr[1].length;
    var xlAbbr = s.match(/^(X+)L$/);
    if (xlAbbr) return 4 + xlAbbr[1].length;

    if (s === 'MEDIUM') return 3;
    return null;
  }

  // ---------- chart resolution ----------
  // Ordered, most specific first. Youth and outerwear must beat the generic
  // "jersey" and "bib" rules, so order is load bearing here.
  var RULES = [
    ['youth-speedsuit', ['youth speedsuit', 'youth skinsuit', 'kids speedsuit']],
    ['youth-bib', ['youth bib', 'kids bib', 'junior bib']],
    ['youth-jersey', ['youth jersey', 'kids jersey', 'junior jersey']],
    ['arm-warmers', ['arm warmer', 'arm screen', 'armwarmer']],
    ['poncho', ['poncho', 'changing robe', 'change robe']],
    ['parka', ['parka']],
    ['swim-womens-one-piece', ['one piece', 'one-piece']],
    ['swim-womens-two-piece', ['two piece', 'two-piece', 'bikini']],
    ['swim-mens', ['jammer', 'competition brief', 'swim brief', 'mens swim', "men's swim"]],
    ['speedsuit', ['speedsuit', 'skinsuit', 'skin suit', 'tt suit', 'time trial suit']],
    ['pro-jersey', ['pro jersey', 'pro aero jersey']],
    ['base-layer', ['base layer', 'baselayer']],
    ['outerwear', ['jacket', 'vest', 'gilet', 'long sleeve', 'longsleeve', 'thermal', 'hoodie jersey', 'wind shell', 'windshell']],
    ['bibs', ['bib', 'shorts', 'knicker', 'tight']],
    ['gloves', ['glove', 'mitt']],
    ['socks', ['sock']],
    ['raceday-bag', ['raceday', 'race day', 'gameday', 'game day', 'musette', 'bag', 'backpack', 'duffel']],
    ['jersey-ss', ['jersey']],
    ['tshirt', ['t-shirt', 'tshirt', 'tee', 'hoodie', 'sweatshirt', 'crewneck', 'polo']]
  ];

  var RULE_PATTERNS = RULES.map(function (r) {
    return r[1].map(function (k) {
      return new RegExp('(?:^|[^a-z])' + k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + 's?(?:[^a-z]|$)');
    });
  });

  function resolveChartId(ctx) {
    if (ctx.explicit) return ctx.explicit;

    var tags = (ctx.tags || []).map(function (t) { return String(t).toLowerCase(); });
    for (var i = 0; i < tags.length; i++) {
      var mt = tags[i].match(/^sizeguide[-_](.+)$/);
      if (mt && findChart(mt[1])) return mt[1];
    }

    var hay = ((ctx.title || '') + ' ' + (ctx.type || '') + ' ' + tags.join(' ')).toLowerCase();
    for (var r = 0; r < RULES.length; r++) {
      var id = RULES[r][0], kws = RULES[r][1];
      for (var k = 0; k < kws.length; k++) {
        if (RULE_PATTERNS[r][k].test(hay)) return id;
      }
    }
    return null;
  }

  // Which group applies to this product. A men's jersey should not offer the
  // women's table, and vice versa. "womens" does not trip the men's test because
  // the m in womens is not on a word boundary.
  function detectGroup(ctx) {
    var hay = ((ctx.title || '') + ' ' + (ctx.type || '') + ' ' + (ctx.tags || []).join(' ')).toLowerCase();
    if (/youth|kids|junior/.test(hay)) return 'youth';
    // Pedal marks women's products explicitly ("women's cut", "WOMEN") and
    // carries no gender tags at all. Across the active catalogue 11 products say
    // women, 4 say men, and 83 say neither, so unmarked means the default cut,
    // which is the men's chart. Falling back to men rather than to "unknown"
    // keeps the women's table off a men's product and the reverse.
    if (/\bwom[ae]n'?s?\b|\bladies\b|\bfemale\b/.test(hay)) return 'women';
    return 'men';
  }

  var ALIASES = {
    'pro-jersey': { chart: 'jersey-ss', label: 'Pro Jersey' },
    'base-layer': { chart: 'jersey-ss', label: 'Base Layer' }
  };

  function findChart(id) {
    if (!DATA) return null;
    for (var i = 0; i < DATA.charts.length; i++) if (DATA.charts[i].id === id) return DATA.charts[i];
    var alias = ALIASES[id];
    if (!alias) return null;
    for (var j = 0; j < DATA.charts.length; j++) {
      if (DATA.charts[j].id === alias.chart) return Object.assign({}, DATA.charts[j], { id: id, label: alias.label });
    }
    return null;
  }

  // ---------- fit engine ----------
  // Deterministic. A size "accommodates" a measurement when the measurement is at
  // or below the top of that size's range. The recommendation is the smallest size
  // that accommodates every measurement given, which is how garments actually work:
  // if your chest needs L and your waist needs M, you need L.

  // Rounding can nudge a value off an exact tie or threshold, so comparisons allow this much slack.
  var EDGE_EPS = 1e-9;

  // Describe how one measurement sits against one cell.
  // centering is 0 dead centre, 1 at the very edge, Infinity when outside.
  function analyzeCell(cell, v) {
    if (!Array.isArray(cell)) return null;
    var rawLo = cell[0];
    var rawHi = cell.length > 1 ? cell[1] : cell[0];
    var lo = rawLo == null ? -Infinity : rawLo;
    var hi = rawHi == null ? Infinity : rawHi;

    var contains = v >= lo && v <= hi;
    var distance = v > hi ? v - hi : (v < lo ? lo - v : 0);

    var centering;
    if (!contains) centering = Infinity;
    else if (!isFinite(lo) || !isFinite(hi) || hi === lo) centering = 0;
    else centering = Math.abs(v - (lo + hi) / 2) / ((hi - lo) / 2);

    return { contains: contains, centering: centering, distance: distance, lo: lo, hi: hi };
  }

  // Best row for a single measurement.
  // Several of Pedal's charts use overlapping bands (outerwear runs 28-34, 30-36,
  // 32-38 and so on), so "first size that fits" would always collapse to the
  // smallest. Among the sizes that contain the measurement, the best is the one
  // where it sits most centrally. When nothing contains it, take the nearest and
  // break ties upward so a shopper is never sent a size too small.
  // A column whose every cell is a single number is really a set of midpoints.
  // Widen each to halfway toward its neighbours. Used by BOTH the decision and
  // the explanation, so the two can never disagree.
  function impliedCell(rows, key, idx) {
    var cells = rows.map(function (r) { return r.v[key]; });
    var allPoints = cells.every(function (c) {
      return Array.isArray(c) && (c.length === 1 || c[0] === c[1]);
    });
    var cell = cells[idx];
    if (!allPoints || !Array.isArray(cell) || !cell.length || cell[0] == null) {
      return { cell: cell, isPoint: false };
    }
    var here = cell[0];
    var prev = idx > 0 && cells[idx - 1] ? cells[idx - 1][0] : null;
    var next = idx < cells.length - 1 && cells[idx + 1] ? cells[idx + 1][0] : null;
    return {
      cell: [prev == null ? -Infinity : here - (here - prev) / 2,
             next == null ? Infinity : here + (next - here) / 2],
      isPoint: true,
      point: here
    };
  }

  function bestForColumn(rows, key, v) {
    // Some Pedal columns publish a single number per size rather than a range
    // (men's jersey waist reads 32, 34, 36). Treated literally, a 33 inch waist
    // sits exactly between two sizes and gets decided by a tie-break, which is
    // arbitrary. A lone value is really the midpoint of a band, so widen it to
    // halfway toward each neighbour before comparing.
    var infos = [];
    rows.forEach(function (row, i) {
      var a = analyzeCell(impliedCell(rows, key, i).cell, v);
      if (a) infos.push({ i: i, a: a });
    });
    if (!infos.length) return null;

    var containing = infos.filter(function (x) { return x.a.contains; });
    if (containing.length) {
      containing.sort(function (a, b) {
        var diff = a.a.centering - b.a.centering;
        return (Math.abs(diff) < EDGE_EPS ? 0 : diff) || a.i - b.i;
      });
      var runnerUp = containing.length > 1 ? containing[1] : null;
      return {
        idx: containing[0].i,
        contained: true,
        ambiguous: !!runnerUp && (runnerUp.a.centering - containing[0].a.centering) < 0.15 - EDGE_EPS,
        infos: infos
      };
    }

    var sorted = infos.slice().sort(function (a, b) {
      return a.a.distance - b.a.distance || b.i - a.i;
    });
    return { idx: sorted[0].i, contained: false, ambiguous: false, infos: infos };
  }

  // Turn "M in Rapha" into body measurements. This only works because both
  // charts are body measurements, so it is a real bridge rather than a guess.
  // Ranges collapse to their midpoint before being fed to the engine.
  function brandMeasurements(brandId, groupId, size) {
    var b = DATA.brands && DATA.brands[brandId];
    if (!b) return null;
    var g = b.groups[groupId] || b.groups.men || b.groups[Object.keys(b.groups)[0]];
    var row = g && g[size];
    if (!row) return null;
    var out = {};
    Object.keys(row).forEach(function (k) {
      var v = row[k];
      if (!Array.isArray(v) || !v.length) return;
      var ends = (v.length > 1 ? [v[0], v[1]] : [v[0]]).filter(function (n) { return typeof n === 'number' && isFinite(n); });
      if (!ends.length) return;
      out[k] = ends.length > 1 ? (ends[0] + ends[1]) / 2 : ends[0];
    });
    return out;
  }

  function brandSizes(brandId, groupId) {
    var b = DATA.brands && DATA.brands[brandId];
    if (!b) return [];
    var g = b.groups[groupId] || b.groups.men || b.groups[Object.keys(b.groups)[0]];
    return g ? Object.keys(g) : [];
  }

  function evaluate(group, measurements, opts) {
    opts = opts || {};
    var bodyCols = group.columns.filter(function (c) { return c.kind === 'body'; });
    var used = bodyCols.filter(function (c) {
      return measurements[c.body] != null && !isNaN(measurements[c.body]);
    });
    if (!used.length) return null;

    var perColumn = [];
    var chosenIdx = 0;
    var primaryIdx = null;
    var fallback = false;
    var borderline = false;

    used.forEach(function (col) {
      var v = measurements[col.body];
      var res = bestForColumn(group.rows, col.key, v);
      if (!res) return;

      // Distinguish "off the end of the chart" from "in a gap between bands".
      var los = [], his = [];
      res.infos.forEach(function (x) {
        if (isFinite(x.a.lo)) los.push(x.a.lo);
        if (isFinite(x.a.hi)) his.push(x.a.hi);
      });
      var beyond = (his.length && v > Math.max.apply(null, his)) ||
                   (los.length && v < Math.min.apply(null, los));
      if (beyond && !res.contained) fallback = true;
      if (res.ambiguous) borderline = true;

      perColumn.push({ col: col, value: v, res: res });
      // On top-half garments the chest decides. That is MAAP's own published rule:
      // where chest and waist point at different sizes, go by chest. Without it a
      // slightly fuller waist quietly pushes riders a size up. On bibs, where waist
      // and hips both genuinely constrain, the largest requirement still wins.
      if (col.primary) {
        primaryIdx = res.idx;
      } else if (res.idx > chosenIdx) {
        chosenIdx = res.idx;
      }
    });

    if (!perColumn.length) return null;

    if (primaryIdx !== null) chosenIdx = primaryIdx;
    var row = group.rows[chosenIdx];

    // Explain the pick against the row actually chosen.
    var perCol = perColumn.map(function (p) {
      var imp = impliedCell(group.rows, p.col.key, chosenIdx);
      var a = analyzeCell(imp.cell, p.value);
      var state = 'in';
      if (a) {
        if (p.value > a.hi) state = 'over';
        else if (p.value < a.lo) state = 'under';
      }
      // Sitting in the top 15% of its band makes the next size up a fair call.
      if (a && a.contains && isFinite(a.lo) && isFinite(a.hi) && a.hi > a.lo) {
        if ((p.value - a.lo) / (a.hi - a.lo) >= 0.85 - EDGE_EPS) borderline = true;
      }
      return {
        key: p.col.key, body: p.col.body, label: p.col.label, value: p.value,
        lo: a ? a.lo : null, hi: a ? a.hi : null, state: state,
        isPoint: imp.isPoint, point: imp.point
      };
    });

    var altIdx = (borderline && !fallback && chosenIdx + 1 < group.rows.length) ? chosenIdx + 1 : null;
    var loose = perCol.some(function (c) { return c.state === 'under'; });

    return {
      best: { row: row, idx: chosenIdx, perCol: perCol },
      alt: altIdx != null ? { row: group.rows[altIdx], idx: altIdx } : null,
      fallback: fallback,
      borderline: borderline,
      loose: loose,
      used: used
    };
  }

  // ---------- rendering ----------

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // The entry that applies to this group: its own group id, else the shared '*' entry.
  function measureFor(chart, group) {
    if (!chart.measure || !group) return null;
    return chart.measure[group.id] || chart.measure['*'] || null;
  }

  function markFor(entry, key) {
    if (!entry) return null;
    for (var i = 0; i < entry.marks.length; i++) {
      if (entry.marks[i].cols.indexOf(key) !== -1) return entry.marks[i];
    }
    return null;
  }

  function badge(letter) {
    var b = el('span', 'PdlSg__badge', letter);
    b.setAttribute('aria-hidden', 'true');
    return b;
  }

  // Midpoint of the chart's middle size, in the given unit, or null when the row has no usable value.
  function exampleFor(group, col, unit) {
    if (!group.rows || !group.rows.length) return null;
    var row = group.rows[Math.floor((group.rows.length - 1) / 2)];
    var val = row && row.v ? row.v[col.key] : null;
    var lo, hi;
    if (typeof val === 'number') {
      lo = val;
      hi = val;
    } else if (Array.isArray(val)) {
      lo = val[0];
      hi = val.length > 1 ? val[1] : val[0];
    } else {
      return null;
    }
    if (typeof lo !== 'number') lo = hi;
    if (typeof hi !== 'number') hi = lo;
    if (typeof lo !== 'number') return null;
    var mid = (lo + hi) / 2;
    return 'e.g. ' + (unit === 'cm' ? Math.round(toCm(mid)) : round(mid, 1));
  }

  function renderMeasure(chart, group, assetBase) {
    var entry = measureFor(chart, group);
    if (!entry || !assetBase) return null;

    var fig = el('figure', 'PdlSg__measure');
    var media = el('div', 'PdlSg__measureMedia');
    media.style.setProperty('--pdlsg-ratio', String(entry.w / entry.h));
    var img = document.createElement('img');
    img.className = 'PdlSg__measureImg';
    img.width = entry.w;
    img.height = entry.h;
    img.alt = entry.alt;
    img.setAttribute('decoding', 'async');
    // The real src waits here until the guide is actually shown.
    img.setAttribute('data-src', assetBase + entry.img);
    media.appendChild(img);
    fig.appendChild(media);

    var steps = el('div', 'PdlSg__measureSteps');
    steps.appendChild(el('h3', 'PdlSg__measureTitle', 'How to measure'));
    var list = el('ol', 'PdlSg__measureList');
    entry.marks.forEach(function (mark) {
      var li = el('li', 'PdlSg__measureStep');
      li.appendChild(badge(mark.l));
      var copy = el('div', 'PdlSg__measureCopy');
      copy.appendChild(el('strong', 'PdlSg__measureLabel', mark.label));
      if (mark.garment) copy.appendChild(el('span', 'PdlSg__tagGarment', 'garment'));
      copy.appendChild(el('span', 'PdlSg__measureText', mark.text));
      li.appendChild(copy);
      list.appendChild(li);
    });
    steps.appendChild(list);
    fig.appendChild(steps);

    return fig;
  }

  function renderTable(group, unit, chart, highlightSize, measure) {
    var wrap = el('div', 'PdlSg__tableWrap');
    var table = el('table', 'PdlSg__table');

    var thead = el('thead');
    var hr = el('tr');
    hr.appendChild(el('th', 'PdlSg__th PdlSg__th--size', 'Size'));
    group.columns.forEach(function (c) {
      var th = el('th', 'PdlSg__th');
      var mark = markFor(measure, c.key);
      if (mark) th.appendChild(badge(mark.l));
      th.appendChild(el('span', null, c.label));
      if (c.kind === 'garment') {
        th.appendChild(el('span', 'PdlSg__tagGarment', 'garment'));
      }
      hr.appendChild(th);
    });
    thead.appendChild(hr);
    table.appendChild(thead);

    var tbody = el('tbody');
    group.rows.forEach(function (row) {
      var tr = el('tr', 'PdlSg__tr');
      tr.setAttribute('data-size', row.size);
      if (highlightSize && row.size === highlightSize) tr.classList.add('is-recommended');

      var td0 = el('td', 'PdlSg__td PdlSg__td--size', row.size);
      tr.appendChild(td0);

      group.columns.forEach(function (c) {
        var unitless = chart.unitless || c.unitless || c.kind === 'text';
        var td = el('td', 'PdlSg__td', fmt(row.v[c.key], unit, unitless));
        td.setAttribute('data-label', c.label);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    wrap.appendChild(table);
    return wrap;
  }

  function renderVideos(chart, root) {
    if (!chart.videos || !chart.videos.length) return null;
    var box = el('div', 'PdlSg__videos');
    box.appendChild(el('h3', 'PdlSg__videosTitle', 'How to choose size'));
    chart.videos.forEach(function (vid) {
      var meta = DATA.videos[vid];
      if (!meta) return;
      var item = el('div', 'PdlSg__videoItem');
      item.style.setProperty('--pdlsg-ratio', meta.ratio);
      var v = document.createElement('video');
      v.className = 'PdlSg__video';
      v.setAttribute('playsinline', '');
      v.setAttribute('controls', '');
      v.setAttribute('preload', 'none');
      v.setAttribute('controlslist', 'nodownload');
      // The poster is fetched the moment it is set, so it waits until the drawer
      // is opened. Two of these were 218KB on every page load for a drawer most
      // visitors never touch.
      if (meta.poster) v.setAttribute('data-poster', meta.poster);
      v.src = meta.src;
      item.appendChild(v);
      var cap = el('figcaption', 'PdlSg__videoCap', meta.label);
      var fig = el('figure', 'PdlSg__videoFig');
      fig.appendChild(item);
      fig.appendChild(cap);
      box.appendChild(fig);
    });
    return box;
  }

  // ---------- component ----------

  function SizeGuide(rootEl) {
    this.root = rootEl;
    this.chartId = rootEl.getAttribute('data-chart-id') || null;
    this.explicit = rootEl.getAttribute('data-chart-explicit') || null;
    this.title = rootEl.getAttribute('data-product-title') || '';
    this.type = rootEl.getAttribute('data-product-type') || '';
    this.tags = (rootEl.getAttribute('data-product-tags') || '').split(',').filter(Boolean);
    this.assetBase = rootEl.getAttribute('data-asset-base') || '';
    this.mode = rootEl.getAttribute('data-mode') || 'product';
    this.groupId = rootEl.getAttribute('data-group') || null;
    this.groupChoice = rootEl.hasAttribute('data-group-choice');
    this.unit = getUnit();
    this.groupIdx = 0;
    this.measurements = this.loadMeasurements();
    this.result = null;
    this.build();
  }

  SizeGuide.prototype.loadMeasurements = function () {
    try {
      var raw = localStorage.getItem(MEASURE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  };

  SizeGuide.prototype.saveMeasurements = function () {
    try { localStorage.setItem(MEASURE_KEY, JSON.stringify(this.measurements)); } catch (e) {}
  };

  SizeGuide.prototype.build = function () {
    var self = this;
    if (this.mode === 'page') {
      this.chart = DATA.charts[0];
    } else {
      var id = this.chartId || resolveChartId({
        explicit: this.explicit, title: this.title, type: this.type, tags: this.tags
      });
      this.chart = findChart(id);
    }

    var trigger = this.root.querySelector('[data-pdlsg-trigger]');
    if (!this.chart) {
      if (trigger) trigger.style.display = 'none';
      return;
    }

    // On a product, show only the group that product is actually sold in.
    this.lockGroup = false;
    if (this.mode !== 'page' && this.chart.groups.length > 1) {
      var want = this.groupId || detectGroup({ title: this.title, type: this.type, tags: this.tags });
      if (want) {
        for (var gi = 0; gi < this.chart.groups.length; gi++) {
          if (this.chart.groups[gi].id === want) {
            this.groupIdx = gi;
            this.lockGroup = !this.groupChoice;
            break;
          }
        }
      }
    }
    if (trigger) {
      trigger.hidden = false;
      trigger.addEventListener('click', function (e) { e.preventDefault(); self.open(); });
    }

    // The theme has several other ways into the size charts: a "Size Charts"
    // link and the "I have reviewed the size chart" checkbox, and at least one
    // of them navigates programmatically, which preventDefault alone cannot
    // stop. Intercept on document in the capture phase so this runs before any
    // other handler, and halt the event outright.
    if (this.mode !== 'page' && !document.documentElement.hasAttribute('data-pdlsg-capture')) {
      document.documentElement.setAttribute('data-pdlsg-capture', '1');
      document.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[href]');
        if (!a) return;
        var href = a.getAttribute('href') || '';
        if (!/size-chart|size-charts|sizing-guide|size_chart/i.test(href)) return;
        if (a.closest('header, nav, footer, .site-nav, .site-header, .site-footer, .mobile-nav, .drawer--header')) return;
        var host = document.querySelector('[data-pdlsg-root][data-mode="product"]');
        if (!host || !host.__pdlsg) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        var wrapper = a.closest('[data-sizechart-checkbox-wrapper]');
        var box = wrapper ? wrapper.querySelector('[data-sizechart-checkbox]') : null;
        host.__pdlsg.open(box || undefined, a);
      }, true);

      document.addEventListener('change', function (e) {
        var box = e.target;
        if (e.pdlsgReviewed) return;
        if (!box.matches || !box.matches('[data-sizechart-checkbox]') || !box.checked) return;
        var host = document.querySelector('[data-pdlsg-root][data-mode="product"]');
        if (!host || !host.__pdlsg || !host.__pdlsg.chart) return;
        host.__pdlsg.open(box);
      });
    }

    this.panel = this.root.querySelector('[data-pdlsg-panel]');
    this.body = this.root.querySelector('[data-pdlsg-body]');
    var closers = this.root.querySelectorAll('[data-pdlsg-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', function (e) { e.preventDefault(); self.requestClose(); });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !self.root.classList.contains('is-open')) return;
      if (!self.nudgeShown()) return self.requestClose();
      self.hideNudge();
      var x = self.panel.querySelector('[data-pdlsg-close]');
      if (x) x.focus();
    });

    this.renderBody();
  };

  SizeGuide.prototype.loadPosters = function () {
    this.root.querySelectorAll('video[data-poster]').forEach(function (v) {
      v.setAttribute('poster', v.getAttribute('data-poster'));
      v.removeAttribute('data-poster');
    });
    this.root.querySelectorAll('img[data-src]').forEach(function (img) {
      img.src = img.getAttribute('data-src');
      img.removeAttribute('data-src');
    });
  };

  SizeGuide.prototype.open = function (checkbox, opener) {
    this.checkbox = checkbox || null;
    this.opener = opener || null;
    this.nudged = false;
    this.root.classList.add('is-open');
    this.loadPosters();
    document.documentElement.classList.add('pdlsg-locked');
    if (this.panel) {
      this.panel.setAttribute('aria-hidden', 'false');
      var focusTarget = this.panel.querySelector('[data-pdlsg-close]');
      if (focusTarget) focusTarget.focus();
    }
  };

  SizeGuide.prototype.gateCheckbox = function () {
    if (this.mode === 'page') return null;
    var sel = '[data-sizechart-checkbox]';
    if (this.checkbox && document.contains(this.checkbox) && this.checkbox.matches(sel)) return this.checkbox;
    var from = this.opener && document.contains(this.opener) ? this.opener : this.root;
    var form = from.closest('form');
    var own = form && form.querySelector(sel);
    if (own) return own;
    var scope = from.closest('.shopify-section') || from.closest('[data-section-id]') || document;
    var boxes = scope.querySelectorAll(sel);
    return boxes.length === 1 ? boxes[0] : null;
  };

  SizeGuide.prototype.markReviewed = function () {
    var gate = this.gateCheckbox();
    if (!gate || gate.checked) return;
    gate.checked = true;
    var reviewed = new Event('change', { bubbles: true });
    reviewed.pdlsgReviewed = true;
    gate.dispatchEvent(reviewed);
  };

  SizeGuide.prototype.close = function () {
    this.hideNudge();
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('pdlsg-locked');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
    this.markReviewed();
    if (this.checkbox) {
      var box = document.contains(this.checkbox) ? this.checkbox : document.querySelector('[data-sizechart-checkbox]');
      try { if (box) box.focus({ preventScroll: true }); } catch (e) {}
      return;
    }
    var t = this.root.querySelector('[data-pdlsg-trigger]');
    if (t) t.focus();
  };

  SizeGuide.prototype.requestClose = function () {
    var group = this.chart.groups[this.groupIdx] || this.chart.groups[0];
    var hasFinder = this.chart.fitEnabled && group.columns.some(function (c) { return c.kind === 'body'; });
    var sized = this.result && this.result.best;
    var gate = this.checkbox || document.querySelector('[data-sizechart-checkbox]');
    if (gate && hasFinder && !sized && !this.nudged) {
      this.showNudge();
      return;
    }
    this.close();
  };

  SizeGuide.prototype.nudgeShown = function () {
    return !!(this.nudge && !this.nudge.hidden);
  };

  SizeGuide.prototype.showNudge = function () {
    var self = this;
    this.nudged = true;
    if (!this.nudge) {
      var id = 'PdlSgNudge-' + Math.random().toString(36).slice(2, 8);
      var box = el('div', 'PdlSg__nudge');
      var card = el('div', 'PdlSg__nudgeCard');
      card.setAttribute('role', 'alertdialog');
      card.setAttribute('aria-modal', 'true');
      card.setAttribute('aria-labelledby', id + '-title');
      card.setAttribute('aria-describedby', id + '-text');
      var title = el('h3', 'PdlSg__nudgeTitle', 'Hey, you sure about that size?');
      title.id = id + '-title';
      var text = el('p', 'PdlSg__nudgeText',
        "Did you really plug in your measurements? Just askin' because we want you to get the right size and not be stuck with something that doesn't fit. Because we love you!");
      text.id = id + '-text';
      var actions = el('div', 'PdlSg__nudgeActions');
      var measure = el('button', 'PdlSg__submit', 'Plug in my measurements');
      measure.type = 'button';
      measure.addEventListener('click', function () {
        self.hideNudge();
        var input = self.body && self.body.querySelector('.PdlSg__input');
        if (!input) return;
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      });
      var sure = el('button', 'PdlSg__ctaClose', "Yep, I'm sure");
      sure.type = 'button';
      sure.addEventListener('click', function () { self.close(); });
      actions.appendChild(measure);
      actions.appendChild(sure);
      card.appendChild(title);
      card.appendChild(text);
      card.appendChild(actions);
      box.appendChild(card);
      this.panel.appendChild(box);
      this.nudge = box;
    }
    this.nudge.hidden = false;
    var first = this.nudge.querySelector('button');
    if (first) first.focus();
  };

  SizeGuide.prototype.hideNudge = function () {
    if (this.nudge) this.nudge.hidden = true;
  };

  SizeGuide.prototype.renderBody = function () {
    var self = this;
    var chart = this.chart;
    var body = this.body;
    if (!body) return;
    body.innerHTML = '';

    var titleEl = this.root.querySelector('[data-pdlsg-title]');
    if (titleEl) titleEl.textContent = chart.label + ' size guide';

    if (this.mode === 'page') {
      var pageTitle = this.root.getAttribute('data-page-title') || 'Sizing Guides';
      body.appendChild(el('h1', 'PdlSg__pageTitle', pageTitle));
      var index = el('div', 'PdlSgIndex');
      DATA.charts.forEach(function (c) {
        var b = el('button', 'PdlSgIndex__btn', c.label);
        b.type = 'button';
        if (c.id === chart.id) b.classList.add('is-active');
        b.addEventListener('click', function () {
          self.chart = c;
          self.groupIdx = 0;
          self.result = null;
          self.renderBody();
        });
        index.appendChild(b);
      });
      body.appendChild(index);
      body.appendChild(el('h2', 'PdlSg__pageChartTitle', chart.label));
    }

    // Lead fit statement, the first thing a shopper should read.
    if (chart.notes && chart.notes.length) {
      var lead = el('p', 'PdlSg__lead');
      lead.textContent = chart.notes[0];
      body.appendChild(lead);
    }

    if (chart.callout) {
      var callout = el('p', 'PdlSg__callout');
      var cut = chart.callout.indexOf(':');
      if (cut > 0 && cut < 20) {
        callout.appendChild(el('strong', null, chart.callout.slice(0, cut + 1)));
        callout.appendChild(document.createTextNode(chart.callout.slice(cut + 1)));
      } else {
        callout.textContent = chart.callout;
      }
      body.appendChild(callout);
    }

    var group = chart.groups[this.groupIdx] || chart.groups[0];

    // Men / Women rides inside the fit finder as a pair of buttons so the
    // choice reads as the first step of finding a size rather than as a tab
    // strip above it. On the standalone page the unit toggle joins it there,
    // stacked underneath; the PDP drawer keeps its unit toggle up top where it
    // has always been. Either control falls back to rendering above when the
    // chart has no finder to host it.
    var hasFinder = chart.fitEnabled && group.columns.some(function (c) { return c.kind === 'body'; });
    var finderHostsUnits = hasFinder && this.mode === 'page';

    if (!chart.unitless && !finderHostsUnits) {
      body.appendChild(this.renderUnits());
    }

    if (chart.groups.length > 1 && !this.lockGroup && !hasFinder) {
      var tabs = el('div', 'PdlSg__tabs');
      tabs.setAttribute('role', 'tablist');
      chart.groups.forEach(function (g, i) {
        var b = el('button', 'PdlSg__tab', g.label);
        b.type = 'button';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', i === self.groupIdx ? 'true' : 'false');
        if (i === self.groupIdx) b.classList.add('is-active');
        b.addEventListener('click', function () {
          self.groupIdx = i;
          self.result = null;
          self.renderBody();
        });
        tabs.appendChild(b);
      });
      body.appendChild(tabs);
    }

    // how to measure figure
    var figure = renderMeasure(chart, group, this.assetBase);
    if (figure) body.appendChild(figure);
    var measure = figure ? measureFor(chart, group) : null;

    // fit finder
    if (chart.fitEnabled) {
      body.appendChild(this.renderFinder(group, measure));
    }

    // table
    var highlight = this.result && this.result.best ? this.result.best.row.size : null;
    body.appendChild(renderTable(group, this.unit, chart, highlight, measure));

    // notes
    if (chart.notes && chart.notes.length > 1) {
      var nb = el('div', 'PdlSg__notes');
      chart.notes.slice(1).forEach(function (n) { nb.appendChild(el('p', 'PdlSg__note', n)); });
      body.appendChild(nb);
    }

    // how to measure
    if (!figure && chart.howTo && chart.howTo.length) {
      var det = el('details', 'PdlSg__howto');
      det.appendChild(el('summary', 'PdlSg__howtoSummary', 'How to measure'));
      var ul = el('ul', 'PdlSg__howtoList');
      chart.howTo.forEach(function (h) { ul.appendChild(el('li', null, h)); });
      det.appendChild(ul);
      body.appendChild(det);
    }

    // videos
    var vids = renderVideos(chart, body);
    if (vids) body.appendChild(vids);
    if (this.mode === 'page' || this.root.classList.contains('is-open')) this.loadPosters();
  };

  SizeGuide.prototype.renderUnits = function () {
    var self = this;
    var toggle = el('div', 'PdlSg__units');
    toggle.setAttribute('role', 'group');
    toggle.setAttribute('aria-label', 'Measurement units');
    ['in', 'cm'].forEach(function (u) {
      var b = el('button', 'PdlSg__unitBtn', u === 'in' ? 'Inches' : 'Centimetres');
      b.type = 'button';
      b.setAttribute('data-unit', u);
      if (self.unit === u) b.classList.add('is-active');
      b.addEventListener('click', function () {
        self.unit = u;
        setUnit(u);
        self.renderBody();
      });
      toggle.appendChild(b);
    });
    return toggle;
  };

  SizeGuide.prototype.renderFinder = function (group, measure) {
    var self = this;
    var wrap = el('div', 'PdlSg__finder');
    var bodyCols = group.columns.filter(function (c) { return c.kind === 'body'; });
    if (!bodyCols.length) return wrap;

    wrap.appendChild(el('h3', 'PdlSg__finderTitle', 'Find my size'));

    if (this.chart.groups.length > 1 && !this.lockGroup) {
      var picker = el('div', 'PdlSg__groups');
      picker.setAttribute('role', 'group');
      picker.setAttribute('aria-label', 'Which chart');
      this.chart.groups.forEach(function (g, i) {
        var gb = el('button', 'PdlSg__groupBtn', g.label);
        gb.type = 'button';
        gb.setAttribute('aria-pressed', i === self.groupIdx ? 'true' : 'false');
        if (i === self.groupIdx) gb.classList.add('is-active');
        gb.addEventListener('click', function () {
          self.groupIdx = i;
          self.result = null;
          self.renderBody();
        });
        picker.appendChild(gb);
      });
      wrap.appendChild(picker);
    }

    if (this.mode === 'page' && !this.chart.unitless) {
      wrap.appendChild(this.renderUnits());
    }

    wrap.appendChild(el('p', 'PdlSg__finderIntro',
      'Enter what you know. Anything you leave blank is simply not used.'));

    var form = el('form', 'PdlSg__form');
    var fields = el('div', 'PdlSg__fields');

    var seen = {};
    bodyCols.forEach(function (col) {
      if (seen[col.body]) return;
      seen[col.body] = true;

      var unitless = self.chart.unitless || col.unitless;
      var f = el('label', 'PdlSg__field');
      var mark = markFor(measure, col.key);
      var lab = el('span', 'PdlSg__fieldLabel', mark ? mark.label : (BODY_LABEL[col.body] || col.label));
      if (mark) lab.insertBefore(badge(mark.l), lab.firstChild);
      f.appendChild(lab);

      var inputRow = el('span', 'PdlSg__inputRow');
      var input = document.createElement('input');
      input.type = 'number';
      input.step = unitless ? '0.5' : '0.1';
      input.min = '0';
      input.className = 'PdlSg__input';
      input.setAttribute('data-body', col.body);
      input.placeholder = unitless ? 'e.g. 9.5' : (exampleFor(group, col, self.unit) || (self.unit === 'cm' ? 'e.g. 96' : 'e.g. 38'));

      var stored = self.measurements[col.body];
      if (stored != null) {
        input.value = unitless ? stored : round(self.unit === 'cm' ? toCm(stored) : stored, 1);
      }
      inputRow.appendChild(input);
      if (!unitless) inputRow.appendChild(el('span', 'PdlSg__inputUnit', self.unit === 'cm' ? 'cm' : 'in'));
      f.appendChild(inputRow);

      if (BODY_HINT[col.body]) f.appendChild(el('span', 'PdlSg__fieldHint', BODY_HINT[col.body]));
      fields.appendChild(f);
    });

    form.appendChild(fields);

    // Most riders have never measured their chest but know their size in another
    // brand. Only offered where the chart actually uses chest/waist/hips.
    var usesBody = bodyCols.some(function (c) {
      return c.body === 'chest' || c.body === 'waist' || c.body === 'hips';
    });
    if (SHOW_BRANDS && usesBody && DATA.brands && Object.keys(DATA.brands).length) {
      var brandIds = Object.keys(DATA.brands);
      var groupId = group.id === 'women' ? 'women' : 'men';

      var bw = el('div', 'PdlSg__brand');
      bw.appendChild(el('span', 'PdlSg__fieldLabel', 'Or use a size you already know'));
      var brow = el('div', 'PdlSg__brandRow');

      var bSel = document.createElement('select');
      bSel.className = 'PdlSg__select';
      bSel.setAttribute('aria-label', 'Brand');
      var opt0 = document.createElement('option');
      opt0.value = '';
      opt0.textContent = 'Choose a brand';
      bSel.appendChild(opt0);
      brandIds.forEach(function (id) {
        var o = document.createElement('option');
        o.value = id;
        o.textContent = DATA.brands[id].label;
        bSel.appendChild(o);
      });

      var sSel = document.createElement('select');
      sSel.className = 'PdlSg__select';
      sSel.setAttribute('aria-label', 'Size in that brand');
      sSel.disabled = true;
      sSel.appendChild(new Option('Size', ''));

      function refreshSizes() {
        sSel.innerHTML = '';
        sSel.appendChild(new Option('Size', ''));
        var sizes = brandSizes(bSel.value, groupId);
        sizes.forEach(function (s) { sSel.appendChild(new Option(s, s)); });
        sSel.disabled = !sizes.length;
      }

      bSel.addEventListener('change', function () {
        refreshSizes();
        self.brandNote = null;
      });

      sSel.addEventListener('change', function () {
        if (!bSel.value || !sSel.value) return;
        var m = brandMeasurements(bSel.value, groupId, sSel.value);
        if (!m) return;
        // Show the derived numbers in the inputs so nothing is hidden from the
        // shopper, then run the same engine the manual path uses.
        var inputs = form.querySelectorAll('.PdlSg__input');
        for (var i = 0; i < inputs.length; i++) {
          var key = inputs[i].getAttribute('data-body');
          if (m[key] != null) {
            inputs[i].value = round(self.unit === 'cm' ? toCm(m[key]) : m[key], 1);
          }
        }
        self.brandNote = DATA.brands[bSel.value].label + ' ' + sSel.value;
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });

      brow.appendChild(bSel);
      brow.appendChild(sSel);
      bw.appendChild(brow);
      bw.appendChild(el('span', 'PdlSg__fieldHint',
        'We convert that brand\u2019s published body measurements, then size you here.'));
      form.appendChild(bw);
    }

    // Aero vs Relaxed only exists where Pedal's own data draws that distinction.
    var hasFitPref = /jersey|speedsuit|swim/.test(this.chart.id);
    if (hasFitPref) {
      var pref = el('div', 'PdlSg__pref');
      pref.appendChild(el('span', 'PdlSg__fieldLabel', 'Preferred fit'));
      var prefRow = el('div', 'PdlSg__prefRow');
      [['aero', 'Aero: skin tight'], ['relaxed', 'Relaxed: looser']].forEach(function (p, i) {
        var l = el('label', 'PdlSg__prefOpt');
        var r = document.createElement('input');
        r.type = 'radio';
        r.name = 'pdlsg-fit-' + self.chart.id;
        r.value = p[0];
        if ((self.fitPref || 'aero') === p[0]) r.checked = true;
        l.appendChild(r);
        l.appendChild(el('span', null, p[1]));
        prefRow.appendChild(l);
      });
      pref.appendChild(prefRow);
      form.appendChild(pref);
    }

    var actions = el('div', 'PdlSg__actions');
    var submit = el('button', 'PdlSg__submit', 'Recommend my size');
    submit.type = 'submit';
    actions.appendChild(submit);
    var reset = el('button', 'PdlSg__reset', 'Clear');
    reset.type = 'button';
    reset.addEventListener('click', function () {
      self.measurements = {};
      self.result = null;
      self.saveMeasurements();
      self.renderBody();
    });
    actions.appendChild(reset);
    form.appendChild(actions);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var inputs = form.querySelectorAll('.PdlSg__input');
      var vals = {};
      var any = false;
      for (var i = 0; i < inputs.length; i++) {
        var raw = parseFloat(inputs[i].value);
        var bodyKey = inputs[i].getAttribute('data-body');
        if (!isNaN(raw) && raw > 0) {
          var col = bodyCols.filter(function (c) { return c.body === bodyKey; })[0];
          var unitless = self.chart.unitless || (col && col.unitless);
          vals[bodyKey] = unitless ? raw : (self.unit === 'cm' ? toIn(raw) : raw);
          any = true;
        }
      }
      if (!any) return;
      var prefEl = form.querySelector('input[name="pdlsg-fit-' + self.chart.id + '"]:checked');
      self.fitPref = prefEl ? prefEl.value : null;
      self.measurements = vals;
      self.saveMeasurements();
      self.result = evaluate(group, vals, { pref: self.fitPref });
      self.renderBody();
      var out = self.root.querySelector('[data-pdlsg-result]');
      if (out) out.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    wrap.appendChild(form);

    if (this.result) wrap.appendChild(this.renderResult(group));
    return wrap;
  };

  SizeGuide.prototype.renderResult = function (group) {
    var self = this;
    var r = this.result;
    var box = el('div', 'PdlSg__result');
    box.setAttribute('data-pdlsg-result', '');

    if (!r || !r.best) {
      box.appendChild(el('p', 'PdlSg__resultNone', 'Enter at least one measurement to get a recommendation.'));
      return box;
    }

    var picked = r.best.row.size;

    // Aero vs relaxed nudges the pick only when the shopper is genuinely between sizes.
    var shown = picked;
    var nudged = false;
    if (r.borderline && r.alt && this.fitPref === 'relaxed') {
      shown = r.alt.row.size;
      nudged = true;
    }

    var head = el('div', 'PdlSg__resultHead');
    if (this.brandNote) {
      box.appendChild(el('p', 'PdlSg__resultFrom', 'Based on ' + this.brandNote + '.'));
    }
    head.appendChild(el('span', 'PdlSg__resultLabel', r.fallback ? 'Closest size' : 'Your size'));
    head.appendChild(el('strong', 'PdlSg__resultSize', shown));
    box.appendChild(head);

    // Just the size. The per-measurement breakdown was noise, and on charts that
    // publish single values it could read as contradicting the recommendation.
    // The notes below still carry anything the shopper needs to act on.

    if (nudged) {
      box.appendChild(el('p', 'PdlSg__resultNote',
        'You are near the top of ' + picked + '. Since you prefer a relaxed fit, ' + shown + ' is the better call. For an aero fit, take ' + picked + '.'));
    } else if (r.borderline && r.alt) {
      box.appendChild(el('p', 'PdlSg__resultNote',
        'You are near the top of ' + picked + '. If you prefer a looser fit, ' + r.alt.row.size + ' will also work.'));
    }

    if (r.fallback) {
      box.appendChild(el('p', 'PdlSg__resultNote PdlSg__resultNote--warn',
        'Your measurements sit outside the published range. ' + shown + ' is the closest we stock. Get in touch and we will help.'));
    }

    if (r.loose && !r.fallback) {
      box.appendChild(el('p', 'PdlSg__resultNote',
        'One of your measurements is below this size’s range, so expect a little room there.'));
    }

    var applied = this.applyToVariant(shown);
    if (applied) {
      box.appendChild(el('p', 'PdlSg__resultApplied', 'Selected ' + applied + ' on this product.'));
      var cta = el('button', 'PdlSg__ctaClose', 'Back to product');
      cta.type = 'button';
      cta.addEventListener('click', function () { self.close(); });
      box.appendChild(cta);
    }

    return box;
  };

  // Best effort: match the recommended size back to whatever this product calls it.
  // Never throws and never blocks the recommendation if the picker is not found.
  SizeGuide.prototype.applyToVariant = function (size) {
    try {
      var wantRank = sizeRank(size);
      var scope = document.querySelector('.Product2025VariantStyles') || document;
      var inputs = scope.querySelectorAll('input[data-variant-input]');
      if (!inputs.length) return null;

      // Work out which option index is the size option.
      var sizeIndex = null;
      var groups = scope.querySelectorAll('fieldset');
      for (var g = 0; g < groups.length; g++) {
        var legend = groups[g].querySelector('legend');
        if (legend && /size/i.test(legend.textContent)) {
          var inp = groups[g].querySelector('input[data-variant-input]');
          if (inp) sizeIndex = inp.getAttribute('data-option-index');
          break;
        }
      }

      var match = null, exact = null;
      for (var i = 0; i < inputs.length; i++) {
        var inp2 = inputs[i];
        if (sizeIndex != null && inp2.getAttribute('data-option-index') !== sizeIndex) continue;
        var val = inp2.value;
        if (String(val).toLowerCase() === String(size).toLowerCase()) { exact = inp2; break; }
        if (wantRank != null && sizeRank(val) === wantRank && !match) match = inp2;
      }

      var target = exact || match;
      if (!target || target.disabled) return null;
      if (!target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return target.value;
    } catch (e) {
      return null;
    }
  };

  // ---------- boot ----------

  function init() {
    var roots = document.querySelectorAll('[data-pdlsg-root]:not([data-pdlsg-ready])');
    if (!roots.length) return;
    for (var i = 0; i < roots.length; i++) {
      roots[i].setAttribute('data-pdlsg-ready', '1');
      try { roots[i].__pdlsg = new SizeGuide(roots[i]); } catch (e) {}
    }
  }

  function mergeData(d) {
    if (!d || !Array.isArray(d.charts)) return;
    if (!DATA) DATA = { version: d.version, sourceUnit: d.sourceUnit, videos: {}, charts: [], brands: {} };
    d.charts.forEach(function (c) {
      for (var i = 0; i < DATA.charts.length; i++) if (DATA.charts[i].id === c.id) return;
      DATA.charts.push(c);
    });
    var own = Object.prototype.hasOwnProperty;
    Object.keys(d.videos || {}).forEach(function (k) { if (!own.call(DATA.videos, k)) DATA.videos[k] = d.videos[k]; });
    Object.keys(d.brands || {}).forEach(function (k) { if (!own.call(DATA.brands, k)) DATA.brands[k] = d.brands[k]; });
  }

  function loadData() {
    var blocks = document.querySelectorAll('script[data-pdlsg-data], script#PdlSgData');
    for (var i = 0; i < blocks.length; i++) {
      try { mergeData(JSON.parse(blocks[i].textContent)); } catch (e) {}
    }
  }

  function boot() {
    loadData();
    if (!DATA) return;
    init();
    document.addEventListener('shopify:section:load', function () { loadData(); init(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.PedalSizeGuide = { sizeRank: sizeRank, resolveChartId: resolveChartId, detectGroup: detectGroup, RULES: RULES, evaluate: evaluate };
})();
